import { AFTextField } from "@/admin/form/AdminFormFields.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import { UuidField } from "@/admin/form/UuidField.tsx";
import { AdminMetalOsSelect } from "@/admin/metal/AdminMetalOsSelect.tsx";
import { leaseFields } from "@/admin/metal/machine/leaseFields.tsx";
import {
    leaseCreateMutation,
    leaseUpdateMutation,
    machineUpdateMutation,
} from "@/admin/metal/machine/machinesQuery.ts";
import type { AdminMachineWithOs } from "@/admin/metal/machine/machinesWithOs.ts";
import { StatusSelector } from "@/admin/metal/machine/StatusSelector.tsx";
import {
    controllerProps,
    formPropsToAriaProps,
    formPropsToFormFieldProps,
} from "@/components/forms/ariaController.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { ShortDate } from "@/components/l10n/DateFormat.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { periodLabels } from "@/modules/metal/offer/period.tsx";
import { statusItems } from "@/modules/metal/status/StatusLabels.tsx";
import { changedFields } from "@/utils/changedFields.ts";
import type { AdminMetalLeaseCreateBody, MetalStatus } from "@warrenio/api-spec/spec.oats.gen";
import { TextField } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { keys, pick } from "remeda";

function isLeasedStatus(status: MetalStatus) {
    return status === "in_use" || status === "pending";
}

export function LeaseModal({ machine }: { machine: AdminMachineWithOs }) {
    interface Inputs extends AdminMetalLeaseCreateBody {
        status: MetalStatus;
    }

    //#region Hooks
    const { location } = machine;

    const leaseCreateDefaults = {
        billing_account_id: undefined!,
        display_name: "Custom machine",
        machine_uuid: machine.uuid,
        requested_os_id: machine.os_id!,
    } satisfies Omit<Inputs, "status">;

    const leaseUpdateFields = machine.lease
        ? {
              ...machine.lease,

              machine_uuid: machine.uuid,
              // Billing account is guaranteed to be set when a lease is present
              billing_account_id: machine.billing_account_id!,
          }
        : undefined;

    const leaseDefaults = leaseUpdateFields ? pick(leaseUpdateFields, keys(leaseCreateDefaults)) : leaseCreateDefaults;

    const form = useForm<Inputs>({
        defaultValues: {
            ...leaseDefaults,

            status: machine.status,
        },
    });
    const { control, watch } = form;

    const machineUpdate = useStandardMutation(machineUpdateMutation);
    const leaseCreate = useStandardMutation(leaseCreateMutation);
    const leaseUpdate = useStandardMutation(leaseUpdateMutation);

    async function onSubmit({ status, ...body }: Inputs) {
        if (isLeasedStatus(status)) {
            if (leaseUpdateFields) {
                const changed = changedFields(leaseUpdateFields, body);
                if (changed) {
                    await leaseUpdate.mutateAsync({
                        body: changed,
                        uuid: leaseUpdateFields.uuid,
                        location,
                    });
                }
            } else {
                // XXX: Lease is created in either "in_use" or "pending" status automatically,
                // but the machine mutation below could overwrite it.
                await leaseCreate.mutateAsync({ body, location });
            }
        }

        if (status !== machine.status) {
            await machineUpdate.mutateAsync({ body: { status }, uuid: machine.uuid, location });
        }
    }
    //#endregion

    const isLeaseEdit = !!leaseUpdateFields;
    const canChangeBillingAccount = !isLeaseEdit;
    const billingCycle = isLeaseEdit ? periodLabels[machine.period_hours ?? 0] : null;

    const leaseForm = (
        <>
            <AFTextField headless control={control} name="display_name" isRequired config={leaseFields.display_name} />
            <Controller
                control={control}
                name="requested_os_id"
                {...controllerProps({ isRequired: true })}
                render={(p) => (
                    <FormField
                        headless
                        wide
                        label={leaseFields.requested_os_id.title}
                        {...formPropsToFormFieldProps(p)}
                    >
                        <AdminMetalOsSelect
                            defaultValueKey={p.field.value}
                            onChange={(item) => p.field.onChange(item?.os_id)}
                        />
                    </FormField>
                )}
            />
            {/* TODO: Better BA selector */}
            <Controller
                control={control}
                name="billing_account_id"
                disabled={!canChangeBillingAccount}
                render={(p) => (
                    <FormField wide headless label="Billing account ID" {...formPropsToFormFieldProps(p)}>
                        <TextField
                            {...formPropsToAriaProps(p)}
                            aria-label="Billing account ID"
                            pattern="\d+"
                            onChange={(v) => p.field.onChange(Number(v))}
                            value={String(p.field.value ?? "")}
                        >
                            <AInput />
                        </TextField>
                    </FormField>
                )}
            />
            <FormField headless wide label="Billing cycle">
                {billingCycle}
            </FormField>
            <FormField headless wide label="End of cycle">
                {machine.ends_at ? <ShortDate date={machine.ends_at} /> : "-"}
            </FormField>
        </>
    );

    return (
        <WModalForm form={form} onSubmit={onSubmit} label="Save" title="Edit machine status">
            <UuidField headless value={leaseUpdateFields?.uuid} />

            <Controller
                control={control}
                name="status"
                {...controllerProps({ isRequired: true })}
                render={(p) => (
                    <StatusSelector items={statusItems} value={p.field.value} onChange={(v) => p.field.onChange(v)} />
                )}
            />

            {isLeasedStatus(watch("status")) && (
                <>
                    <h2
                        // XXX: How to get proper padding here without hardcoding...
                        className="pt-2"
                    >
                        Lease details
                    </h2>
                    {leaseUpdateFields && (
                        <>
                            <FormField headless wide label="User ID">
                                {leaseUpdateFields.user_id}
                            </FormField>
                            <FormField headless wide label="Created at">
                                {leaseUpdateFields.created_at}
                            </FormField>
                        </>
                    )}
                    {leaseForm}
                </>
            )}
        </WModalForm>
    );
}
